import { IconClose } from '@consta/icons/IconClose'
import { IconSearchStroked } from '@consta/icons/IconSearchStroked'
import { rcTreeAdapter } from '@consta/rc-tree-adapter/rcTreeAdapter'
import { Button } from '@consta/uikit/Button'
import { Text } from '@consta/uikit/Text'
import { TextField } from '@consta/uikit/TextField'
import { addFieldMarkersToProjectApi, addProjectCubeApi, addProjectWellsApi } from '@core/core/api'
import {
  clearHotStorageLoadedData,
  getCubes,
  getMarkersAndAddToProject,
  loadHotStorageFieldsHavingMarkers,
  loadHotStorageFieldsHavingWellsData,
  loadHotStorageMarkerNamesForField,
  loadHotStorageWellsHavingDataForField,
  selectActiveProject,
  selectActiveProjectName,
  selectAllCubes,
  selectHotStorageFieldsWithMarkers,
  selectHotStorageFieldsWithWells,
  useAppDispatch,
  useAppSelector,
} from '@core/core/store'
import type { SpaEventId } from '@core/core/types'
import { getDataBaseCustomParams } from '@core/core/utils'
import { useSpa } from '@core/hooks'
import type { TreeProps } from 'rc-tree'
import { default as RCTree } from 'rc-tree'
import type { FC } from 'react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'

import {
  cubeNode,
  cubesRoot,
  fieldNode,
  markerNode,
  markersRoot,
  wellNode,
  wellsLimit,
  wellsRoot,
} from './constants'
import css from './GeoDbImport.css'
import { checkAddEntityDeletedToTrash, getTreeLeafKeys } from './helpers'
import { getTreeData } from './treeData'
import type { TypedDataNode } from './types'

type Props = {
  onImportSidebarClose: () => void
}

const treeProps = rcTreeAdapter({ size: 'm' })

export const GeoDbImport: FC<Props> = ({ onImportSidebarClose }) => {
  const sendSpaEvent = useSpa()
  const [showSearchField, setShowSearchField] = useState<boolean>(false)
  const [searchObjectName, setSearchObjectName] = useState<string>('')
  const [checkedEntities, setCheckedEntities] = useState<TypedDataNode[]>([])
  const fieldsWithWells = useAppSelector(selectHotStorageFieldsWithWells)
  const fieldsWithMarkers = useAppSelector(selectHotStorageFieldsWithMarkers)
  const cubes = useAppSelector(selectAllCubes)
  const activeProject = useAppSelector(selectActiveProject)
  const activeProjectName = useAppSelector(selectActiveProjectName)
  const dispatch = useAppDispatch()

  const sendSpaEvents = (count: number, dataAnalyticsId: SpaEventId) => {
    if (count) sendSpaEvent(dataAnalyticsId, getDataBaseCustomParams(count, activeProjectName))
  }

  const handleAddProject = () => {
    const addedWellsIds = checkedEntities
      .filter(node => node.type === wellNode)
      .map(node => node.id)

    const wellsCount = addedWellsIds.length
    if (wellsCount > 0) {
      sendSpaEvents(wellsCount, 'hot storage_add well')
      addProjectWellsApi(activeProject.id, addedWellsIds)
        .then(() => {
          sendSpaEvents(wellsCount, 'hot storage_well load')
        })
        .catch(console.error)
    }

    const addedMarkerNodes = checkedEntities.filter(node => node.type === markerNode)
    if (addedMarkerNodes.length > 0) {
      sendSpaEvents(addedMarkerNodes.length, 'hot storage_add marker')

      const isFiltered = showSearchField && searchObjectName.trim().length > 0
      // Проходимся по месторождениям с прогруженными на фронт маркерами
      fieldsWithMarkers
        .filter(field => field.markerNames !== undefined)
        .forEach(field => {
          const fieldId = field.id
          const isFieldSelected = checkedEntities.some(
            node =>
              node.type === fieldNode && node.id === fieldId && node.key.startsWith(markersRoot),
          )

          if (isFieldSelected && !isFiltered) {
            // Грузим все маркера по месторождению
            addFieldMarkersToProjectApi(activeProject.id, [fieldId])
              .then(addedIds => {
                sendSpaEvents(addedIds.length, 'hot storage_marker load')
              })
              .catch(console.error)
          } else {
            // Грузим по именам
            const markerNames = addedMarkerNodes
              .filter(node => node.key.includes(fieldId))
              .map(node => node.id)

            if (markerNames.length > 0) {
              dispatch(
                getMarkersAndAddToProject(activeProject.id, fieldId, markerNames, count => {
                  sendSpaEvents(count, 'hot storage_marker load')
                }),
              )
            }
          }
        })
    }

    const addedCubesIds = checkedEntities
      .filter(node => node.type === cubeNode)
      .map(node => node.id)

    const cubesCount = addedCubesIds.length
    if (cubesCount > 0) {
      sendSpaEvents(cubesCount, 'hot storage_add seismic')
      addedCubesIds.forEach(cubeId => {
        addProjectCubeApi(activeProject.id, cubeId)
          .then(() => {
            sendSpaEvents(1, 'hot storage_seismic load')
          })
          .catch(error => {
            checkAddEntityDeletedToTrash(activeProject.id, cubeId, 'cube')
            console.error(error)
          })
      })
    }

    // Сбросим галки после нажатия кнопки
    setCheckedEntities([])
  }

  const filter = showSearchField ? searchObjectName : ''
  const treeData = useMemo(
    () =>
      getTreeData({
        fieldsWithWells,
        fieldsWithMarkers,
        filter,
        wellsLimit,
        cubes,
      }),
    [fieldsWithWells, fieldsWithMarkers, cubes, showSearchField, searchObjectName],
  )

  const onNodeCheck = useCallback<NonNullable<TreeProps<TypedDataNode>['onCheck']>>(
    (keys, info) => {
      setCheckedEntities(info.checkedNodes)
    },
    [],
  )

  // При изменении данных оставляем галки только на узлах, соответствующим листьям нового дерева.
  // Нужно из-за бага дерева - при перестроении по новым данным оно не внутри галки пересчитывает, но событием не стреляет.
  // При изменениях данных по дозагрузке узлыы только добавляются, на галки не влияет, поэтому пасем только фильтр.
  useLayoutEffect(() => {
    const keys = getTreeLeafKeys(treeData)
    setCheckedEntities(old => old.filter(node => keys.includes(node.key)))
  }, [filter])

  // Возвращаем галки обратно в дерево, чтобы наше представление и визуал не расходились
  const checkedKeys = useMemo(
    () => checkedEntities.filter(node => node.isLeaf).map(node => node.key),
    [checkedEntities],
  )

  const onExpand: TreeProps<TypedDataNode>['onExpand'] = (_, info) => {
    const { node, expanded } = info
    if (expanded) {
      if (node.key === wellsRoot) {
        if (fieldsWithWells.length === 0) dispatch(loadHotStorageFieldsHavingWellsData())
      } else if (node.key === markersRoot) {
        if (fieldsWithMarkers.length === 0) dispatch(loadHotStorageFieldsHavingMarkers())
      } else if (node.key === cubesRoot) {
        if (cubes.length === 0) dispatch(getCubes())
      } else if (node.type === fieldNode) {
        if (node.key.startsWith(wellsRoot)) {
          const fieldId = node.id
          const isLoaded = fieldsWithWells.find(field => field.id === fieldId)?.wells !== undefined
          if (!isLoaded) dispatch(loadHotStorageWellsHavingDataForField(fieldId))
        } else if (node.key.startsWith(markersRoot)) {
          const fieldId = node.id
          const isLoaded =
            fieldsWithMarkers.find(field => field.id === fieldId)?.markerNames !== undefined
          if (!isLoaded) dispatch(loadHotStorageMarkerNamesForField(fieldId))
        }
      }
    }
  }

  // При закрытии очищаем хранилище, чтобы при следующем открытии иметь возможность грузить обновленные данные
  // Иного механизма обновления пока нет
  useEffect(() => {
    return () => {
      dispatch(clearHotStorageLoadedData())
    }
  }, [])

  return (
    <div className={css.geoDbImport}>
      <div className={css.dbWrapper}>
        <div className={css.dbContainer}>
          <Text size="s" view="secondary">
            База данных
          </Text>
          <div>
            <Button
              size="s"
              iconSize="xs"
              iconRight={IconSearchStroked}
              onlyIcon={true}
              view="clear"
              onClick={() => {
                setShowSearchField(!showSearchField)
              }}
            />
            <Button
              size="s"
              iconSize="xs"
              iconRight={IconClose}
              onlyIcon={true}
              view="clear"
              onClick={onImportSidebarClose}
            />
          </div>
        </div>
        <div className={css.findWrapper}>
          {!!showSearchField && (
            <TextField
              size="s"
              leftSide={IconSearchStroked}
              value={searchObjectName}
              placeholder="Найти объект"
              className={css.searchText}
              onChange={value => {
                setSearchObjectName(value ?? '')
              }}
            />
          )}
        </div>
      </div>
      <div className={css.info}>
        <Text size="xs" view="secondary">
          Отображаются первые 100 скважин на месторождении
        </Text>
      </div>
      <div className={css.geoDbImportTree}>
        <RCTree
          {...treeProps}
          checkedKeys={checkedKeys}
          treeData={treeData}
          checkable={true}
          onCheck={onNodeCheck}
          onExpand={onExpand}
        />
      </div>
      <Button
        label="Добавить в проект"
        size="s"
        className={css.btnAdd}
        onClick={handleAddProject}
      />
    </div>
  )
}
